/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RootState, AppDispatch } from '../../../../../../app/redux/store'
import {
  getIntegrationStatus,
  initiateIntegration,
  disconnectIntegration,
  toggleInventorySync,
  syncInventoryNow,
} from '../../../../../../app/redux/features/integration/integrationAction'
import { clearSyncResult } from '../../../../../../app/redux/features/integration/integrationSlice'

const ConnectedAccounts: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>()
  const {
    connected, connectedAt, loading, initiating, disconnecting, error,
    inventorySyncEnabled, lastSyncAt, togglingSync, syncing, syncResult,
    amazonTokenRevoked, reauthUrl,
  } = useSelector((state: RootState) => state.integrationSlice)

  useEffect(() => {
    dispatch(getIntegrationStatus())
    const params = new URLSearchParams(window.location.search)
    const integrationResult = params.get('integration')
    if (integrationResult) {
      window.history.replaceState({}, document.title, window.location.pathname)
    }
  }, [dispatch])

  // Auto-clear syncResult after 8 seconds
  useEffect(() => {
    if (syncResult) {
      const t = setTimeout(() => dispatch(clearSyncResult()), 8000)
      return () => clearTimeout(t)
    }
  }, [syncResult, dispatch])

  const handleConnect = async () => {
    const result = await dispatch(initiateIntegration())
    if (initiateIntegration.fulfilled.match(result)) {
      const url = result.payload?.url
      if (url) window.location.href = url
    }
  }

  const handleDisconnect = () => {
    if (window.confirm('Sei sicuro di voler disconnettere CaleffiPricing?')) {
      dispatch(disconnectIntegration())
    }
  }

  const handleToggleSync = () => {
    dispatch(toggleInventorySync(!inventorySyncEnabled))
  }

  const handleSyncNow = () => {
    dispatch(syncInventoryNow())
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return null
    return new Date(dateStr).toLocaleString('it-IT', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    })
  }

  return (
    <div className='card mb-5 mb-xl-10'>
      <div
        className='card-header border-0 cursor-pointer'
        role='button'
        data-bs-toggle='collapse'
        data-bs-target='#kt_account_connected_accounts'
        aria-expanded='true'
        aria-controls='kt_account_connected_accounts'
      >
        <div className='card-title m-0'>
          <h3 className='fw-bolder m-0'>Account Collegati</h3>
        </div>
      </div>

      <div id='kt_account_connected_accounts' className='collapse show'>
        <div className='card-body border-top p-9'>

          {error && (
            <div className='alert alert-danger d-flex align-items-center p-5 mb-6'>
              <span className='svg-icon svg-icon-2hx svg-icon-danger me-3'></span>
              <div className='d-flex flex-column'>
                <h4 className='mb-1 text-danger'>Errore</h4>
                <span>{error}</span>
              </div>
            </div>
          )}

          {/* Amazon token revoked banner */}
          {connected && amazonTokenRevoked && (
            <div
              className='alert d-flex align-items-center p-4 mb-5'
              style={{ background: '#fff8e1', border: '1.5px solid #f59e0b', borderRadius: 10 }}
            >
              <span style={{ fontSize: 22, marginRight: 12 }}>⚠️</span>
              <div className='flex-grow-1'>
                <div className='fw-bold text-dark mb-1'>La connessione Amazon è scaduta</div>
                <div className='text-muted fs-7'>
                  Il token Amazon è stato revocato. Devi riautorizzare l&apos;accesso per continuare a sincronizzare l&apos;inventario.
                </div>
              </div>
              <a
                href={reauthUrl || 'https://caleffipricing.com/connect'}
                target='_blank'
                rel='noopener noreferrer'
                className='btn btn-sm btn-warning ms-4'
                style={{ whiteSpace: 'nowrap' }}
              >
                Riautorizza
              </a>
            </div>
          )}

          {/* CaleffiPricing row */}
          <div className='py-2'>
            <div className='d-flex flex-stack'>
              <div className='d-flex align-items-center'>
                {/* Icon */}
                <div
                  className='symbol symbol-50px me-5'
                  style={{ background: '#f5f8ff', borderRadius: 8, padding: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  <svg width='32' height='32' viewBox='0 0 32 32' fill='none' xmlns='http://www.w3.org/2000/svg'>
                    <rect width='32' height='32' rx='6' fill='#1d4ed8' />
                    <path d='M8 16h16M16 8v16' stroke='white' strokeWidth='2.5' strokeLinecap='round' />
                  </svg>
                </div>

                <div className='d-flex flex-column'>
                  <span className='fs-5 text-dark fw-bolder'>CaleffiPricing</span>
                  <div className='fs-6 fw-bold text-gray-400'>
                    {loading ? (
                      'Verifica in corso...'
                    ) : connected ? (
                      <>
                        <span className='badge badge-light-success me-2'>Connesso</span>
                        {connectedAt && (
                          <span className='text-muted'>dal {new Date(connectedAt).toLocaleDateString('it-IT')}</span>
                        )}
                      </>
                    ) : (
                      'Collega il tuo account Amazon repricing'
                    )}
                  </div>
                </div>
              </div>

              {/* Action button */}
              <div className='d-flex justify-content-end align-items-center'>
                {loading ? (
                  <span className='spinner-border spinner-border-sm text-primary' />
                ) : connected ? (
                  <button
                    className='btn btn-sm btn-light-danger'
                    onClick={handleDisconnect}
                    disabled={disconnecting}
                  >
                    {disconnecting ? (
                      <><span className='spinner-border spinner-border-sm me-2' />Disconnessione...</>
                    ) : (
                      'Disconnetti'
                    )}
                  </button>
                ) : (
                  <button
                    className='btn btn-sm btn-primary'
                    onClick={handleConnect}
                    disabled={initiating}
                  >
                    {initiating ? (
                      <><span className='spinner-border spinner-border-sm me-2' />Avvio...</>
                    ) : (
                      'Collega CaleffiPricing'
                    )}
                  </button>
                )}
              </div>
            </div>

            {/* ── Inventory Sync Panel (visible only when connected) ── */}
            {connected && (
              <div
                className='mt-5 p-5 rounded'
                style={{ background: '#f8f9fa', border: '1px solid #e9ecef' }}
              >
                <div className='d-flex flex-stack align-items-center'>
                  <div className='d-flex align-items-center'>
                    <span className='svg-icon svg-icon-2 me-3 text-primary'>
                      {/* refresh icon */}
                      <svg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
                        <polyline points='1 4 1 10 7 10'></polyline>
                        <path d='M3.51 15a9 9 0 1 0 .49-3.58'></path>
                      </svg>
                    </span>
                    <div>
                      <div className='fw-bold text-dark fs-6'>Sincronizzazione inventario</div>
                      <div className='text-muted fs-7'>
                        Aggiorna automaticamente le quantità da CaleffiPricing (EAN/barcode)
                      </div>
                    </div>
                  </div>

                  {/* Toggle switch */}
                  <div className='form-check form-switch form-check-custom form-check-solid ms-4'>
                    <input
                      className='form-check-input'
                      type='checkbox'
                      checked={inventorySyncEnabled}
                      onChange={handleToggleSync}
                      disabled={togglingSync}
                      style={{ width: '3rem', height: '1.5rem', cursor: 'pointer' }}
                    />
                  </div>
                </div>

                {/* Sub-row: last sync + sync now button */}
                {inventorySyncEnabled && (
                  <div className='d-flex flex-stack align-items-center mt-4'>
                    <div className='text-muted fs-7'>
                      {lastSyncAt ? (
                        <>Ultima sincronizzazione: <strong>{formatDate(lastSyncAt)}</strong></>
                      ) : (
                        'Nessuna sincronizzazione eseguita'
                      )}
                    </div>
                    <button
                      className='btn btn-sm btn-light-primary'
                      onClick={handleSyncNow}
                      disabled={syncing}
                    >
                      {syncing ? (
                        <><span className='spinner-border spinner-border-sm me-2' />Sincronizzazione...</>
                      ) : (
                        <>
                          <svg className='me-1' xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'>
                            <polyline points='1 4 1 10 7 10'></polyline>
                            <path d='M3.51 15a9 9 0 1 0 .49-3.58'></path>
                          </svg>
                          Sincronizza ora
                        </>
                      )}
                    </button>
                  </div>
                )}

                {/* Sync result banner */}
                {syncResult && (
                  <div className='alert alert-success d-flex align-items-center p-3 mt-3 mb-0'>
                    <span className='me-2'>✓</span>
                    <span className='fs-7'>
                      Completata: <strong>{syncResult.synced}</strong> prodotti aggiornati su {syncResult.total}
                      {syncResult.errors > 0 && (
                        <span className='text-warning ms-2'>({syncResult.errors} errori)</span>
                      )}
                    </span>
                  </div>
                )}
              </div>
            )}
            {/* end inventory sync panel */}

          </div>
          {/* end row */}

        </div>
      </div>
    </div>
  )
}

export { ConnectedAccounts }

